import { useRef } from 'react';
import { css } from '@emotion/react';
import { createFileRoute } from '@tanstack/react-router';
import { CreatePinForm } from '@frontend/account';
import { authnClient, navigateToUrl } from '@frontend/auth';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Heading, Text } from '@frontend/design-system';
import { FormWrap } from '../../components/form-wrap';
import { recordPinEnrollmentResult, recordPinEnrollmentTimeToComplete } from '../../services/auth-metrics';
import { handlePostAuthRedirect } from '../../services/route-utils';

export const Route = createFileRoute('/pin-enrollment/')({
  validateSearch: (search: Record<string, unknown>) => ({
    redirectTo: typeof search.redirectTo === 'string' ? search.redirectTo : undefined,
  }),
  component: PinEnrollmentPage,
  loader: async (): Promise<Awaited<ReturnType<typeof authnClient.fetchUser>> | null> => {
    try {
      const user = await authnClient.fetchUser();
      return user ?? null;
    } catch (error) {
      recordPinEnrollmentResult('failure');
      navigateToUrl(appConfig.DEFAULT_REDIRECT, {
        reason: 'PIN enrollment failed to load route due to a network error',
        error,
      });
    }
    return null;
  },
});

function PinEnrollmentPage() {
  const { t } = useTranslation('SignIn');
  const { redirectTo } = Route.useSearch();
  const user = Route.useLoaderData() as Awaited<ReturnType<typeof authnClient.fetchUser>> | null;
  const routerNavigate = Route.useNavigate();
  const startedAt = useRef(performance.now());
  const hasCompleted = useRef(false);

  const handleComplete = () => {
    if (hasCompleted.current) {
      return;
    }
    hasCompleted.current = true;
    recordPinEnrollmentResult('success');
    recordPinEnrollmentTimeToComplete(performance.now() - startedAt.current);
    handlePostAuthRedirect(redirectTo ?? appConfig.DEFAULT_REDIRECT, routerNavigate, {
      reason: 'pin enrollment complete',
    });
  };

  return (
    <FormWrap>
      <Heading
        css={css`
          margin-bottom: ${theme.spacing(2)};
          text-align: center;
        `}
      >
        {user?.firstName
          ? t("Hi {{name}}! Let's create your PIN.", { name: user.firstName })
          : t("Let's create your PIN.")}
      </Heading>
      <Text
        css={css`
          margin-bottom: ${theme.spacing(4)};
          text-align: center;
        `}
      >
        {t('Your PIN is a faster way to get back into Weave. You can use it on any computer in your office.')}
      </Text>
      <CreatePinForm onSubmit={handleComplete} submitLabel={t('Set PIN and Continue')} />
    </FormWrap>
  );
}
